import { useState } from "react";

import { ErrorApi, actualizarProyecto } from "../api/client";
import type { Proyecto, TextoLiteral } from "../api/types";
import { EditorTextoLiteral } from "./EditorTextoLiteral";
import estilos from "./PanelTextoLiteral.module.css";

interface Props {
  token: string;
  proyecto: Proyecto;
  deshabilitado: boolean;
  onGuardado: (proyecto: Proyecto) => void;
}

function mensajeDeError(err: unknown, generico: string): string {
  if (err instanceof ErrorApi) return err.message;
  if (err instanceof Error) return err.message;
  return generico;
}

// Edicion de los literales inmutables (precio, telefono, fecha, termino
// legal, M6) de un proyecto YA creado, via PATCH /projects/{id}. Copia local
// inicializada UNA vez desde `proyecto.texto_usuario` (no se resincroniza en
// cada poll de VistaProyecto -- mismo criterio que EditorVisualFlyer con
// `documento`) para no descartar ediciones sin guardar mientras el usuario
// escribe.
export function PanelTextoLiteral({
  token,
  proyecto,
  deshabilitado,
  onGuardado,
}: Props): JSX.Element {
  const [valores, setValores] = useState<TextoLiteral[]>(
    () => proyecto.texto_usuario ?? [],
  );
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizados = valores
    .map((t) => ({
      valor: t.valor.trim(),
      etiqueta: t.etiqueta?.trim() || undefined,
    }))
    .filter((t) => t.valor.length > 0);
  const guardados = (proyecto.texto_usuario ?? []).map((t) => ({
    valor: t.valor,
    etiqueta: t.etiqueta ?? undefined,
  }));
  const hayCambios = JSON.stringify(normalizados) !== JSON.stringify(guardados);

  const guardar = async (): Promise<void> => {
    if (guardando || !hayCambios) return;
    setGuardando(true);
    setError(null);
    try {
      const actualizado = await actualizarProyecto(token, proyecto.id, {
        texto_usuario: normalizados,
      });
      setValores(actualizado.texto_usuario ?? []);
      onGuardado(actualizado);
    } catch (err) {
      setError(mensajeDeError(err, "No se pudo guardar el texto literal"));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <section className={estilos.tarjeta} aria-labelledby="titulo-texto-literal">
      <header className={estilos.encabezado}>
        <h3 id="titulo-texto-literal">Texto literal</h3>
        <p className={estilos.subtitulo}>
          Precios, telefonos, fechas o terminos que deben salir EXACTOS en el
          flyer; el resto del copy lo redacta la IA.
        </p>
      </header>

      <EditorTextoLiteral
        idBase={`texto-literal-${proyecto.id}`}
        valores={valores}
        onCambiar={setValores}
        deshabilitado={deshabilitado || guardando}
      />

      {deshabilitado && (
        <p className={estilos.nota}>
          Hay una generacion en curso; espera a que termine para editar.
        </p>
      )}

      {error && (
        <p className={estilos.mensajeError} role="alert">
          {error}
        </p>
      )}

      <div className={estilos.pie}>
        <button
          type="button"
          className="boton boton-primario"
          onClick={() => void guardar()}
          disabled={deshabilitado || guardando || !hayCambios}
        >
          {guardando ? "Guardando..." : "Guardar texto literal"}
        </button>
      </div>
    </section>
  );
}
